import React, { useEffect, useMemo, useState } from "react";
import axiosInstance from "../axiosConfig";

const formatTime = value => {
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return "-";
  return new Intl.DateTimeFormat("en-IN", {
    timeZone: "Asia/Kolkata",
    day: "2-digit",
    month: "2-digit",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
    hour12: true,
  }).format(date).replace(/\b(am|pm)\b/gi, value => value.toUpperCase());
};

export default function RedWorkerTimeModal({ employeeId, mode = "interest", slot, onClose, onSaved }) {
  const [availableSlots, setAvailableSlots] = useState([]);
  const [selectedStart, setSelectedStart] = useState("");
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState("");

  const lotId = slot?.DisplayLotId || slot?.LotId || "-";
  const selectedSlot = useMemo(
    () => availableSlots.find(item => String(item.start) === selectedStart),
    [availableSlots, selectedStart]
  );

  useEffect(() => {
    let active = true;
    const loadSlots = async () => {
      setLoading(true);
      setError("");
      try {
        const fixedSlot = slot?.CalendarSlotSequence == null ? null : {
          start: slot.ScheduledStart,
          end: slot.ScheduledEnd,
          label: "Assigned Work Time",
          available: true,
        };
        const response = fixedSlot ? null : await axiosInstance.get("/api/red-support-slots/calendar");
        const slots = fixedSlot ? [fixedSlot] : (response.data?.slots || []).filter(item => item.available);
        if (!active) return;
        setAvailableSlots(slots);
        const currentStart = String(slot?.PreferredScheduledStart || "");
        setSelectedStart(slots.some(item => String(item.start) === currentStart) ? currentStart : "");
      } catch (requestError) {
        if (active) setError(requestError.response?.data?.message || "Available work time load nahi ho saka.");
      } finally {
        if (active) setLoading(false);
      }
    };
    loadSlots();
    return () => { active = false; };
  }, [slot]);

  const submit = async event => {
    event.preventDefault();
    if (!selectedStart) {
      setError("Ek available work time select karein.");
      return;
    }
    setSubmitting(true);
    setError("");
    try {
      const payload = { preferredScheduledStarts: [selectedStart] };
      if (mode === "update") {
        await axiosInstance.patch(`/api/red-support-slots/${slot.Id}/interest-availability`, payload);
      } else {
        await axiosInstance.post(`/api/red-support-slots/${slot.Id}/interest`, {
          workerId: employeeId,
          ...payload,
        });
        if (slot.notification_id) {
          await axiosInstance.patch(`/api/red-support-slots/notifications/${slot.notification_id}/read`);
        }
      }
      await onSaved?.({ selectedStart, selectedSlot, mode });
      onClose();
    } catch (requestError) {
      setError(requestError.response?.data?.message || (mode === "update"
        ? "Available work time update nahi hua."
        : "Interested request nahi bhej sake."));
    } finally {
      setSubmitting(false);
    }
  };

  return <div className="modal d-block" role="dialog" aria-modal="true" aria-labelledby="redWorkerTimeModalTitle" style={{ backgroundColor: "rgba(15, 23, 42, 0.72)", zIndex: 3100 }}>
    <div className="modal-dialog modal-dialog-centered modal-dialog-scrollable mx-auto my-2 my-sm-4" style={{ width: "calc(100% - 20px)", maxWidth: 680 }}>
      <form className="modal-content border-0 shadow-lg rounded-4 overflow-hidden" onSubmit={submit} style={{ maxHeight: "calc(100vh - 20px)" }}>
        <div className="modal-header border-0 px-3 px-sm-4 pt-4 pb-2">
          <div>
            <h4 className="modal-title fw-bold mb-1" id="redWorkerTimeModalTitle">
              {mode === "update" ? "Available Work Time Change Karein" : "Apna Available Work Time Select Karein"}
            </h4>
            <div className="text-muted">Lot {lotId} ke liye sirf ek time select karein.</div>
          </div>
          <button type="button" className="btn-close" aria-label="Close" disabled={submitting} onClick={onClose} />
        </div>

        <div className="modal-body px-3 px-sm-4 py-3">
          {error && <div className="alert alert-danger py-2" role="alert">{error}</div>}
          {loading ? <div className="text-center py-5 text-muted">
            <span className="spinner-border text-primary mb-3" aria-hidden="true" />
            <div>Available work time load ho raha hai...</div>
          </div> : !availableSlots.length ? <div className="alert alert-info mb-0">
            Abhi koi future RED work time available nahi hai. Thodi der baad dobara check karein.
          </div> : <div className="d-grid gap-2">
            {availableSlots.map((item, index) => {
              const inputId = `red-worker-time-${slot.Id}-${index}`;
              const checked = selectedStart === String(item.start);
              return <label key={item.start} htmlFor={inputId} className={`d-flex align-items-center gap-3 border rounded-3 p-3 ${checked ? "border-primary bg-primary-subtle" : "bg-white"}`} style={{ cursor: "pointer" }}>
                <input
                  className="form-check-input flex-shrink-0 m-0"
                  id={inputId}
                  name={`red-worker-time-${slot.Id}`}
                  type="radio"
                  value={item.start}
                  checked={checked}
                  disabled={submitting}
                  onChange={event => { setSelectedStart(event.target.value); setError(""); }}
                />
                <span>
                  <strong className="d-block">{item.label || `Time ${index + 1}`}</strong>
                  <span className="text-muted small">{formatTime(item.start)} – {formatTime(item.end)}</span>
                </span>
              </label>;
            })}
          </div>}
        </div>

        <div className="modal-footer border-0 px-3 px-sm-4 pt-2 pb-4">
          <button type="button" className="btn btn-outline-secondary px-4" disabled={submitting} onClick={onClose}>Cancel</button>
          <button type="submit" className="btn btn-primary px-4 fw-semibold" disabled={loading || submitting || !selectedStart}>
            {submitting ? <><span className="spinner-border spinner-border-sm me-2" aria-hidden="true" />Saving...</> : mode === "update" ? "Confirm New Time" : "Confirm & Interested"}
          </button>
        </div>
      </form>
    </div>
  </div>;
}
